import { isDescriptiveBoatPath } from '../../utils/boat-url.util';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';

type Consent = 'granted' | 'denied' | null;
type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  [key: `ga-disable-${string}`]: boolean;
};
const CONSENT_KEY = 'lcys.analytics-consent.v1';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly measurementId = environment.googleAnalyticsMeasurementId.trim();
  private readonly consent = signal<Consent>(this.readConsent());
  readonly preferenceOpen = signal(false);
  private initialized = false;
  private currentPage: URL | null = null;
  private currentItem: { id: number; name: string; path: string } | null = null;
  private sentItemKey: string | null = null;

  constructor() {
    this.preferenceOpen.set(this.configured && this.consent() === null);
  }

  get configured(): boolean {
    return /^G-[A-Z0-9]+$/i.test(this.measurementId);
  }

  get doNotTrack(): boolean {
    const value = this.document.defaultView?.navigator.doNotTrack;
    return value === '1' || value?.toLowerCase() === 'yes';
  }

  setConsent(granted: boolean): void {
    const previous = this.consent();
    const choice = granted && !this.doNotTrack ? 'granted' : 'denied';
    this.consent.set(choice);
    this.preferenceOpen.set(false);
    try {
      this.document.defaultView?.localStorage.setItem(CONSENT_KEY, choice);
    } catch { /* A storage-restricted browser can still choose for this session. */ }
    const win = this.document.defaultView as AnalyticsWindow | null;
    if (win && this.configured) {
      win[`ga-disable-${this.measurementId}`] = choice !== 'granted';
      if (this.initialized) win.gtag?.('consent', 'update', { analytics_storage: choice });
    }
    if (choice === 'denied') {
      this.clearAnalyticsCookies();
      this.sentItemKey = null;
    }
    if (choice === 'granted' && previous !== 'granted' && this.currentPage) {
      this.trackPageView(this.currentPage.pathname);
      this.sendCurrentItemView();
    }
  }

  clearPage(): void {
    this.currentPage = null;
    this.currentItem = null;
    this.sentItemKey = null;
    const win = this.document.defaultView as AnalyticsWindow | null;
    if (win && this.configured) win[`ga-disable-${this.measurementId}`] = true;
  }

  trackPageView(url: string): void {
    const page = new URL(url, environment.publicUrl);
    page.search = '';
    page.hash = '';
    const previousPath = this.currentPage?.pathname;
    this.currentPage = this.isPublicPath(page.pathname) ? page : null;
    if (!this.currentPage || previousPath !== this.currentPage.pathname) {
      this.currentItem = null;
      this.sentItemKey = null;
    }
    if (!this.currentPage || !this.canTrack()) return;
    this.send('page_view', {});
  }

  trackLead(formName: 'contact' | 'listing_inquiry', itemId?: number): void {
    if (!this.currentPage || !this.canTrack()) return;
    this.send('generate_lead', {
      form_name: formName,
      ...(itemId ? { item_id: String(itemId) } : {}),
    });
  }

  trackItemView(itemId: number, itemName: string): void {
    if (!this.currentPage || !Number.isSafeInteger(itemId) || itemId <= 0) return;
    // Remember only the current public listing, so consent given on its page
    // records that view without replaying visits made before consent elsewhere.
    this.currentItem = { id: itemId, name: itemName, path: this.currentPage.pathname };
    this.sendCurrentItemView();
  }

  private sendCurrentItemView(): void {
    const item = this.currentItem;
    if (!item || item.path !== this.currentPage?.pathname || !this.canTrack()) return;
    const key = `${item.path}:${item.id}`;
    if (this.sentItemKey === key) return;
    this.send('view_item', {
      items: [{ item_id: String(item.id), item_name: item.name }],
    });
    this.sentItemKey = key;
  }

  private isPublicPath(path: string): boolean {
    return ['/', '/about', '/contact', '/yachts', '/merchandises', '/privacy-policy', '/terms-of-service'].includes(path) ||
      /^\/(boats|merchandise)\/\d+$/.test(path) || isDescriptiveBoatPath(path);
  }

  private canTrack(): boolean {
    if (!isPlatformBrowser(this.platformId) || !this.configured || this.doNotTrack ||
        this.consent() !== 'granted') return false;
    const win = this.document.defaultView as AnalyticsWindow | null;
    if (!win) return false;
    win[`ga-disable-${this.measurementId}`] = false;
    this.initialize(win);
    return true;
  }

  private initialize(win: AnalyticsWindow): void {
    if (this.initialized) return;
    win.dataLayer = win.dataLayer || [];
    // Google tag commands use the standard Arguments queue format.
    win.gtag = function () { win.dataLayer?.push(arguments); };
    win.gtag('consent', 'default', {
      analytics_storage: 'granted', ad_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied',
    });
    win.gtag('js', new Date());
    win.gtag('config', this.measurementId, {
      send_page_view: false,
      debug_mode: !environment.production,
      cookie_domain: this.document.location.hostname,
      page_location: this.currentPage?.toString(),
      page_referrer: this.referrerOrigin(),
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.measurementId)}`;
    this.document.head.appendChild(script);
    this.initialized = true;
  }

  private referrerOrigin(): string {
    try { return new URL(this.document.referrer).origin; } catch { return ''; }
  }

  private readConsent(): Consent {
    try {
      const value = this.document.defaultView?.localStorage.getItem(CONSENT_KEY);
      return value === 'granted' || value === 'denied' ? value : null;
    } catch { return null; }
  }

  private clearAnalyticsCookies(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    for (const cookie of this.document.cookie.split(';')) {
      const name = cookie.split('=')[0].trim();
      if (!/^_ga(?:_|$)/.test(name)) continue;
      for (const domain of ['', `; domain=${this.document.location.hostname}`, '; domain=.leaguecityyachtsales.com']) {
        this.document.cookie = `${name}=; Max-Age=0; path=/${domain}; SameSite=Lax; Secure`;
      }
    }
  }

  private send(event: string, parameters: Record<string, unknown>): void {
    (this.document.defaultView as AnalyticsWindow | null)?.gtag?.('event', event, {
      page_location: this.currentPage?.toString(),
      page_title: this.document.title,
      page_referrer: this.referrerOrigin(),
      ...parameters,
    });
  }
}
